import React from "react";
import {
  Box,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import {
  getSwayyShipmentNumber,
  SwayyShipment,
} from "../../../../../models/SwayyShipment/SwayyShipment";
import {
  getSwayyShipmentStatusLabel,
  getSwayyShipmentStatusTone,
} from "../../../../../models/SwayyShipment/SwayyShipmentFilters";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { swayyShipmentStore } from "../../../stores/SwayyShipmentStore";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { SwayyTableEmptyState } from "../SwayyPageShell";
import {
  swayyTableBodyCellLeftSx,
  swayyTableBodyCellSx,
  swayyTableHeadCellLeftSx,
  swayyTableHeadCellSx,
  swayyTableHeadRowSx,
} from "../swayyTableStyles";

export type SwayyShipmentAction = "view" | "open_order";

const PAGE_SIZE_OPTIONS: FilterValue[] = [
  { id: "10", value: "10 / page" },
  { id: "20", value: "20 / page" },
  { id: "50", value: "50 / page" },
  { id: "100", value: "100 / page" },
];

const thSx = swayyTableHeadCellSx;
const thLeftSx = swayyTableHeadCellLeftSx;
const tdSx = swayyTableBodyCellSx;
const tdLeftSx = swayyTableBodyCellLeftSx;

const shipmentIdSx = {
  fontFamily: swayyFont,
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  cursor: "pointer",
  "&:hover": { color: swayyColors.pine },
};

const StatusBadge = ({ status }: { status: string }) => {
  const tone = getSwayyShipmentStatusTone(status);
  const styles: Record<string, { bg: string; color: string }> = {
    pine: { bg: swayyColors.pineLight, color: swayyColors.deepGreen },
    success: { bg: "rgba(30, 166, 114, 0.12)", color: swayyColors.success },
    warning: { bg: "rgba(217, 119, 6, 0.12)", color: swayyColors.warning },
    danger: { bg: "rgba(220, 38, 38, 0.1)", color: swayyColors.danger },
    muted: { bg: swayyColors.pageBg, color: swayyColors.textSecondary },
  };
  const toneStyle = styles[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: swayyRadii.full,
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: swayyFont,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {getSwayyShipmentStatusLabel(status)}
    </Box>
  );
};

const getShipmentItemsSummary = (shipment: SwayyShipment) => {
  const items = shipment.items || [];
  const totalQty = items.reduce((sum, item) => sum + (item.qty || 0), 0);
  if (items.length === 0) {
    return { name: "—", sku: "", qtyLabel: "—", extraCount: 0 };
  }
  const first = items[0];
  return {
    name: first.name || "Item",
    sku: first.sku || "",
    qtyLabel: String(totalQty),
    extraCount: Math.max(0, items.length - 1),
  };
};

interface SwayyShipmentsTableProps {
  onAction: (action: SwayyShipmentAction, shipment: SwayyShipment) => void;
  onRetry?: () => void;
}

const SwayyShipmentsTable: React.FC<SwayyShipmentsTableProps> = ({
  onAction,
  onRetry,
}) => {
  const store = swayyShipmentStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const total = store.pagination.total || 0;
  const page = store.params.page;
  const limit = store.params.limit || 20;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pageCount = Math.max(1, Math.ceil(total / limit) || 1);

  const paginationFooter = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${swayyColors.border}`,
      }}
    >
      <CustomTypography
        variant="body3"
        color={swayyColors.textSecondary}
        sx={{ fontFamily: swayyFont, fontSize: 12 }}
      >
        Showing {from}–{to} of {total} shipments
      </CustomTypography>

      <Pagination
        count={pageCount}
        page={page}
        onChange={(_, next) => store.handlePageChange(next)}
        shape="rounded"
        size="small"
        sx={{
          "& .MuiPaginationItem-root": {
            fontFamily: swayyFont,
            color: swayyColors.deepGreen,
            borderRadius: swayyRadii.sm,
          },
          "& .Mui-selected": {
            bgcolor: `${swayyColors.pine} !important`,
            color: `${swayyColors.white} !important`,
            "&:hover": { bgcolor: `${swayyColors.pineHover} !important` },
          },
        }}
      />

      <Box sx={{ minWidth: 120 }}>
        <AppSearchDropdownMulti
          title="Rows"
          isMultiSelect={false}
          list={PAGE_SIZE_OPTIONS}
          value={PAGE_SIZE_OPTIONS.filter(
            (option) => option.id === String(limit),
          )}
          local
          fontSize="12px"
          minWidth="120px"
          handleSelect={(items) => {
            store.handleRowsPerPageChange(Number(items[0]?.id || 20));
          }}
        />
      </Box>
    </Box>
  );

  const renderEmpty = () => (
    <SwayyTableEmptyState
      title="No shipments found"
      description="No shipments match these filters. Try adjusting search or filters."
    />
  );

  if (!store.loading && store.error && store.shipments.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: swayyRadii.lg,
          border: `1px solid ${swayyColors.border}`,
          overflow: "hidden",
          bgcolor: swayyColors.white,
        }}
      >
        <SwayyTableEmptyState
          title="Couldn't load shipments"
          description={store.error}
          actionLabel="Try again"
          onAction={onRetry}
          danger
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: swayyRadii.lg,
        border: `1px solid ${swayyColors.border}`,
        overflow: "hidden",
        bgcolor: swayyColors.white,
      }}
    >
      {isMobile ? (
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          {store.loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={110}
                  sx={{ borderRadius: swayyRadii.md }}
                />
              ))
            : store.shipments.length === 0
              ? renderEmpty()
              : store.shipments.map((shipment) => (
                  <Box
                    key={shipment.id}
                    sx={{
                      p: 1.75,
                      borderRadius: swayyRadii.md,
                      border: `1px solid ${swayyColors.border}`,
                      bgcolor: swayyColors.pageBg,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        onClick={() => onAction("view", shipment)}
                        sx={{ cursor: "pointer", maxWidth: "100%", minWidth: 0 }}
                      >
                        <SkuCopyRow
                          sku={getSwayyShipmentNumber(shipment)}
                          emptyFallback="—"
                          copyLabel="Copy shipment ID"
                          color={swayyColors.deepGreen}
                          fontWeight={700}
                          typographySx={{
                            ...shipmentIdSx,
                            fontSize: 14,
                          }}
                        />
                      </Box>
                      <StatusBadge status={String(shipment.status)} />
                    </Box>
                    {(() => {
                      const itemsSummary = getShipmentItemsSummary(shipment);
                      return (
                        <>
                          <CustomTypography
                            variant="body3"
                            color={swayyColors.deepGreen}
                            sx={{
                              fontFamily: swayyFont,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {itemsSummary.name}
                            {itemsSummary.extraCount > 0
                              ? ` +${itemsSummary.extraCount} more`
                              : ""}
                          </CustomTypography>
                          {itemsSummary.sku ? (
                            <Box sx={{ mt: 0.25 }}>
                              <SkuCopyRow
                                sku={itemsSummary.sku}
                                emptyFallback="—"
                                copyLabel="Copy SKU"
                                color={swayyColors.pine}
                                fontWeight={600}
                                typographySx={{
                                  fontFamily: swayyFont,
                                  fontSize: 11,
                                }}
                              />
                            </Box>
                          ) : null}
                          <CustomTypography
                            variant="body3"
                            color={swayyColors.textSecondary}
                            sx={{
                              fontFamily: swayyFont,
                              fontSize: 11,
                              mt: 0.2,
                            }}
                          >
                            Qty {itemsSummary.qtyLabel}
                          </CustomTypography>
                        </>
                      );
                    })()}
                    <CustomTypography
                      variant="body3"
                      color={swayyColors.textSecondary}
                      sx={{ fontFamily: swayyFont, fontSize: 12, mt: 0.5 }}
                    >
                      {shipment.order_number || `Order #${shipment.order_id}`} ·{" "}
                      {shipment.carrier || "No carrier"}
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      color={swayyColors.textSecondary}
                      sx={{ fontFamily: swayyFont, fontSize: 12, mt: 0.35 }}
                    >
                      AWB: {shipment.awb || "—"}
                      {shipment.invoice
                        ? ` · Invoice ${shipment.invoice.status}`
                        : ""}
                    </CustomTypography>
                    <Box sx={{ mt: 1.25 }}>
                      <AppButton
                        label="Order"
                        onClick={() => onAction("open_order", shipment)}
                        customStyles={{
                          minHeight: 40,
                          borderRadius: swayyRadii.sm,
                          bgcolor: "transparent",
                          color: swayyColors.deepGreen,
                          border: `1px solid ${swayyColors.border}`,
                          fontSize: 12,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={swayyTableHeadRowSx}>
                <TableCell sx={thLeftSx}>Shipment</TableCell>
                <TableCell sx={thSx}>Order</TableCell>
                <TableCell sx={thLeftSx}>Items</TableCell>
                <TableCell sx={thSx}>Qty</TableCell>
                <TableCell sx={thSx}>Carrier</TableCell>
                <TableCell sx={thSx}>AWB</TableCell>
                <TableCell sx={thSx}>Status</TableCell>
                <TableCell sx={thSx}>Invoice</TableCell>
                <TableCell sx={thSx}>Expected</TableCell>
                <TableCell sx={thSx}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.loading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 10 }).map((__, cell) => (
                        <TableCell
                          key={cell}
                          sx={cell === 0 || cell === 2 ? tdLeftSx : tdSx}
                        >
                          <Skeleton height={22} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : store.shipments.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ border: 0 }}>
                          {renderEmpty()}
                        </TableCell>
                      </TableRow>
                    )
                  : store.shipments.map((shipment) => {
                      const itemsSummary = getShipmentItemsSummary(shipment);
                      return (
                      <TableRow
                        key={shipment.id}
                        hover
                        sx={{
                          "&:hover": { bgcolor: swayyColors.pineLight },
                        }}
                      >
                        <TableCell sx={tdLeftSx}>
                          <Box
                            onClick={() => onAction("view", shipment)}
                            sx={{ cursor: "pointer", maxWidth: "100%" }}
                          >
                            <SkuCopyRow
                              sku={getSwayyShipmentNumber(shipment)}
                              emptyFallback="—"
                              copyLabel="Copy shipment ID"
                              color={swayyColors.deepGreen}
                              fontWeight={700}
                              typographySx={shipmentIdSx}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <Box
                            onClick={() => onAction("open_order", shipment)}
                            sx={{ cursor: "pointer", maxWidth: "100%" }}
                          >
                            <SkuCopyRow
                              sku={
                                shipment.order_number ||
                                `#${shipment.order_id}`
                              }
                              emptyFallback="—"
                              copyLabel="Copy order"
                              color={swayyColors.pine}
                              fontWeight={600}
                              typographySx={{
                                fontFamily: swayyFont,
                                fontSize: 13,
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                                "&:hover": { color: swayyColors.pineHover },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...tdLeftSx, minWidth: 160, maxWidth: 240 }}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{
                              fontFamily: swayyFont,
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            {itemsSummary.name}
                            {itemsSummary.extraCount > 0
                              ? ` +${itemsSummary.extraCount} more`
                              : ""}
                          </CustomTypography>
                          {itemsSummary.sku ? (
                            <Box sx={{ mt: 0.25 }}>
                              <SkuCopyRow
                                sku={itemsSummary.sku}
                                emptyFallback="—"
                                copyLabel="Copy SKU"
                                color={swayyColors.pine}
                                fontWeight={600}
                                typographySx={{
                                  fontFamily: swayyFont,
                                  fontSize: 12,
                                }}
                              />
                            </Box>
                          ) : null}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{ fontFamily: swayyFont, fontWeight: 600 }}
                          >
                            {itemsSummary.qtyLabel}
                          </CustomTypography>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{ fontFamily: swayyFont, fontWeight: 500 }}
                          >
                            {shipment.carrier || "—"}
                          </CustomTypography>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <SkuCopyRow
                            sku={shipment.awb}
                            emptyFallback="—"
                            copyLabel="Copy AWB"
                            color={swayyColors.pine}
                            fontWeight={600}
                            typographySx={{
                              fontFamily: swayyFont,
                              fontSize: 13,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <StatusBadge status={String(shipment.status)} />
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {shipment.invoice ? (
                            <CustomTypography
                              variant="body3"
                              color={swayyColors.textSecondary}
                              sx={{
                                fontFamily: swayyFont,
                                fontSize: 12,
                                textTransform: "capitalize",
                              }}
                            >
                              #{shipment.invoice.id} · {shipment.invoice.status}
                            </CustomTypography>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {shipment.expected_at
                            ? formatDate(shipment.expected_at)
                            : "—"}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {shipment.created_at
                            ? formatDate(shipment.created_at)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                    })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {store.shipments.length > 0 && paginationFooter}
    </Paper>
  );
};

export default observer(SwayyShipmentsTable);
